import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  activateGuideline,
  createGuidelineDraft,
  evaluateGuideline,
  listClientProfiles,
  listGuidelines,
  retireGuideline,
  saveClientProfile,
  suggestClientProfile,
} from '../../services/mailIntelApi';
import {
  GateRulesSection,
  TriageSettingsSection,
  UsageSection,
} from './MailConfigSections';
import {
  Btn,
  Chip,
  DetailShell,
  EmptyNote,
  ErrorBanner,
  SectionLabel,
  Spinner,
  T,
  humanise,
} from './shared';

const errText = (e) => String(e?.message || e);
const isForbidden = (e) => /403|forbidden|permission/i.test(errText(e));

const INPUT =
  'w-full rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-[13px] text-zinc-100 outline-none focus:border-[#5652CF] disabled:opacity-60';

const EMPTY_PROFILE = {
  client_key: '',
  style: '',
  salutation: '',
  sign_off: '',
  escalation_channel: '',
  dislikes: [],
  notes: '',
};

// ---- client communication profiles ---------------------------------------

const ProfileForm = ({
  profile,
  isNew,
  onSave,
  onCancel,
  saving,
  onSuggest,
}) => {
  const [form, setForm] = useState({
    ...EMPTY_PROFILE,
    ...profile,
    dislikesText: (profile.dislikes || []).join('\n'),
  });
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-zinc-700/60 bg-zinc-800/30 p-3">
      <label className="text-[11px] text-zinc-400">
        Client mail domain
        <input
          aria-label="Client mail domain"
          className={INPUT}
          value={form.client_key}
          onChange={set('client_key')}
          placeholder="acme.com"
          disabled={!isNew}
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-zinc-400">
          Style
          <select
            aria-label="Style"
            className={INPUT}
            value={form.style || ''}
            onChange={set('style')}
          >
            <option value="">No preference</option>
            <option value="terse">Short and direct</option>
            <option value="narrative">Full sentences</option>
          </select>
        </label>
        <label className="text-[11px] text-zinc-400">
          Escalation
          <select
            aria-label="Escalation"
            className={INPUT}
            value={form.escalation_channel || ''}
            onChange={set('escalation_channel')}
          >
            <option value="">No preference</option>
            <option value="call">Offer a call</option>
            <option value="mail">Stay on mail</option>
          </select>
        </label>
        <label className="text-[11px] text-zinc-400">
          Greeting
          <input
            aria-label="Greeting"
            className={INPUT}
            value={form.salutation || ''}
            onChange={set('salutation')}
            placeholder="Hi Priya"
          />
        </label>
        <label className="text-[11px] text-zinc-400">
          Sign-off
          <input
            aria-label="Sign-off"
            className={INPUT}
            value={form.sign_off || ''}
            onChange={set('sign_off')}
            placeholder="Best regards"
          />
        </label>
      </div>
      <label className="text-[11px] text-zinc-400">
        Phrases they dislike (one per line)
        <textarea
          aria-label="Phrases they dislike"
          className={INPUT}
          rows={3}
          value={form.dislikesText}
          onChange={set('dislikesText')}
        />
      </label>
      <div className="flex justify-end gap-2">
        {onSuggest && (
          <Btn
            variant="ghost"
            disabled={saving || !form.client_key.trim()}
            onClick={async () => {
              const s = await onSuggest(form.client_key.trim().toLowerCase());
              if (!s) return;
              // Fill only empty fields: what the PM typed always wins.
              setForm((f) => ({
                ...f,
                style: f.style || s.style || '',
                salutation: f.salutation || s.salutation || '',
                sign_off: f.sign_off || s.sign_off || '',
                dislikesText: f.dislikesText || (s.dislikes || []).join('\n'),
              }));
            }}
          >
            Suggest from my sent mail
          </Btn>
        )}
        <Btn variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          disabled={saving || !form.client_key.trim()}
          onClick={() =>
            onSave(form.client_key.trim().toLowerCase(), {
              style: form.style || null,
              salutation: form.salutation || null,
              sign_off: form.sign_off || null,
              escalation_channel: form.escalation_channel || null,
              notes: form.notes || null,
              dislikes: form.dislikesText
                .split('\n')
                .map((d) => d.trim())
                .filter(Boolean),
            })
          }
        >
          {saving ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </div>
  );
};

const ClientProfilesSection = ({ accessToken }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // profile, or 'new'
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listClientProfiles(accessToken);
      setData(res?.data || { profiles: [] });
    } catch (e) {
      setError(errText(e));
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key, fields) => {
    setSaving(true);
    try {
      await saveClientProfile(accessToken, key, fields);
      toast.success(`Profile for ${key} saved`);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(`Could not save: ${errText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!data) return <Spinner />;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-zinc-400">
        How each client likes to be written to. Drafts to that client follow it;
        the rules that always apply still win.
        {!data.can_edit && ' Only a project PM can change these.'}
      </p>
      {data.can_edit && !editing && (
        <div>
          <Btn onClick={() => setEditing('new')}>Add client</Btn>
        </div>
      )}
      {editing && (
        <ProfileForm
          key={editing === 'new' ? 'new' : editing.client_key}
          profile={editing === 'new' ? EMPTY_PROFILE : editing}
          isNew={editing === 'new'}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
          onSuggest={async (key) => {
            try {
              const res = await suggestClientProfile(accessToken, key);
              if (!res.data.enough_evidence) {
                toast.error(
                  `Not enough of your sent mail to ${key} yet (${res.data.evidence.sent_mails}).`,
                );
                return null;
              }
              toast.success(
                `Suggested from ${res.data.evidence.sent_mails} sent mails. Review, then save.`,
              );
              return res.data.suggestion;
            } catch (e) {
              toast.error(errText(e));
              return null;
            }
          }}
        />
      )}
      {data.profiles.length === 0 ? (
        <EmptyNote>
          No client profiles yet. Drafts use the general rules.
        </EmptyNote>
      ) : (
        <div className="flex flex-col gap-2">
          {data.profiles.map((p) => (
            <div
              key={p.client_key}
              className="flex items-start justify-between gap-3 rounded-xl border border-zinc-700/60 bg-zinc-800/30 p-2.5"
            >
              <div className="min-w-0 text-[12px] text-zinc-300">
                <p className="text-[13px] font-semibold text-zinc-100">
                  {p.client_key}{' '}
                  <span className="text-[11px] font-normal text-zinc-500">
                    v{p.version}
                  </span>
                </p>
                <p>
                  {[
                    p.style && humanise(p.style),
                    p.salutation && `“${p.salutation}”`,
                    p.sign_off && `“${p.sign_off}”`,
                    p.escalation_channel &&
                      `escalate by ${p.escalation_channel}`,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'No preferences set'}
                </p>
                {p.dislikes?.length > 0 && (
                  <p className="text-zinc-500">
                    Avoid: {p.dislikes.join(', ')}
                  </p>
                )}
              </div>
              {data.can_edit && (
                <Btn variant="ghost" onClick={() => setEditing(p)}>
                  Edit
                </Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- guideline modules ----------------------------------------------------

const STATUS_TONE = {
  active: { fg: T.ok, bg: T.okBg },
  draft: { fg: T.warn, bg: T.warnBg },
  retired: { fg: T.muted, bg: 'rgba(161,161,170,0.10)' },
};

const ModuleRow = ({
  module,
  canManage,
  onDraft,
  onActivate,
  onRetire,
  onEval,
  busy,
}) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(module.fragment);
  const tone = STATUS_TONE[module.status] || STATUS_TONE.retired;
  return (
    <div className="rounded-xl border border-zinc-700/60 bg-zinc-800/30 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-zinc-100">
          {humanise(module.value)}
        </span>
        <span className="text-[11px] text-zinc-500">v{module.version}</span>
        <Chip fg={tone.fg} bg={tone.bg}>
          {module.status}
        </Chip>
        {canManage && !editing && (
          <span className="ml-auto flex gap-1">
            {module.status !== 'retired' && (
              <Btn
                variant="ghost"
                onClick={() => setEditing(true)}
                disabled={busy}
              >
                New version
              </Btn>
            )}
            {module.status === 'draft' && (
              <Btn
                variant="ghost"
                onClick={() => onEval(module.ref)}
                disabled={busy}
              >
                Run eval
              </Btn>
            )}
            {module.status === 'draft' && (
              <Btn
                onClick={() => onActivate(module.ref)}
                disabled={busy || !module.eval?.passed}
              >
                Activate
              </Btn>
            )}
            {module.status !== 'retired' && module.created_by !== 'seed' && (
              <Btn
                variant="danger"
                onClick={() => onRetire(module.ref)}
                disabled={busy}
              >
                Retire
              </Btn>
            )}
          </span>
        )}
      </div>
      {module.status === 'draft' && (
        <p
          className={`mt-1 text-[11px] ${
            module.eval?.passed ? 'text-emerald-300' : 'text-amber-300'
          }`}
        >
          {module.eval
            ? `Eval ${module.eval.passed ? 'passed' : 'failed'}: ${
                module.eval.passed_cases
              }/${module.eval.cases} cases.`
            : 'Run the eval before activating (about a minute).'}
        </p>
      )}
      {editing ? (
        <div className="mt-2 flex flex-col gap-2">
          <textarea
            aria-label={`New version of ${module.dimension} ${module.value}`}
            className={INPUT}
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Btn
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              Cancel
            </Btn>
            <Btn
              variant="primary"
              disabled={busy || !text.trim() || text === module.fragment}
              onClick={async () => {
                if (await onDraft(module, text)) setEditing(false);
              }}
            >
              Save as draft
            </Btn>
          </div>
        </div>
      ) : (
        <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-300">
          {module.fragment}
        </p>
      )}
    </div>
  );
};

const GuidelinesSection = ({ accessToken }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listGuidelines(accessToken);
      setData(res?.data || null);
    } catch (e) {
      setError(
        isForbidden(e)
          ? 'Only mail admins can see the guideline modules.'
          : errText(e),
      );
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      toast.success(success);
      await load();
      return true;
    } catch (e) {
      toast.error(errText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBanner>{error}</ErrorBanner>;
  if (!data) return <Spinner />;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[13px] text-zinc-400">
        Small instructions the draft writer stacks in this order. A new version
        is a draft until you activate it; activating retires the old one. Floors
        always apply and nothing below them can relax them.
      </p>
      {data.precedence.map((dimension) => {
        const modules = data.modules.filter((m) => m.dimension === dimension);
        if (!modules.length) return null;
        return (
          <section key={dimension}>
            <SectionLabel count={modules.length}>
              {dimension === 'floor'
                ? 'Floors (always on)'
                : humanise(dimension)}
            </SectionLabel>
            <div className="flex flex-col gap-2">
              {modules.map((m) => (
                <ModuleRow
                  key={m.ref}
                  module={m}
                  canManage={data.can_manage}
                  busy={busy}
                  onDraft={(mod, text) =>
                    run(
                      () =>
                        createGuidelineDraft(
                          accessToken,
                          mod.dimension,
                          mod.value,
                          text,
                        ),
                      'Draft version saved. Activate it to use it.',
                    )
                  }
                  onActivate={(ref) =>
                    run(() => activateGuideline(accessToken, ref), 'Activated')
                  }
                  onRetire={(ref) =>
                    run(() => retireGuideline(accessToken, ref), 'Retired')
                  }
                  onEval={(ref) =>
                    run(
                      () => evaluateGuideline(accessToken, ref),
                      'Eval finished',
                    )
                  }
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};

/** Mail settings drawer: client profiles (PM), guideline modules, triage
 * settings and gate rules (admin). */
const MailSettingsPanel = ({ accessToken, onClose }) => {
  const [tab, setTab] = useState('clients');
  return (
    <DetailShell
      eyebrow="Mail intelligence"
      title="Settings"
      onClose={onClose}
      chips={
        <div className="flex gap-1" role="tablist">
          {[
            ['clients', 'Client profiles'],
            ['guidelines', 'Guidelines'],
            ['triage', 'Triage'],
            ['rules', 'Gate rules'],
            ['usage', 'Usage'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${
                tab === id
                  ? 'bg-[#5652CF] text-white'
                  : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      }
    >
      {tab === 'clients' && <ClientProfilesSection accessToken={accessToken} />}
      {tab === 'guidelines' && <GuidelinesSection accessToken={accessToken} />}
      {tab === 'triage' && <TriageSettingsSection accessToken={accessToken} />}
      {tab === 'rules' && <GateRulesSection accessToken={accessToken} />}
      {tab === 'usage' && <UsageSection accessToken={accessToken} />}
    </DetailShell>
  );
};

export default MailSettingsPanel;
